import WebViewer, { Core, WebViewerInstance } from '@pdftron/webviewer';
import { APRYZE_STATIC_URL } from '@smx-ui-common/consts';
import { getViewOnlyToolBar } from '@smx-ui-common/viewer-appryze-tool/get-view-only-tool-bar';
import { useAccess } from '@smx-ui-permission/hook/use-access';
import { SmxPermissionContext } from '@smx-ui-permission/provider/permission-provider';
import { PermissionCombinationType } from '@smx-ui-permission/types';
import { Spin } from 'antd';
import { useContext, useEffect, useRef, useState } from 'react';

import { API } from '../../../../service/drawings/src/ui/common';
import { DrawingSheetOrDocumentIdDataType } from '../../../../service/drawings/src/ui/types/types';
import { UI_ELEMENTS } from './constants/ui-elements';
import { CommitOutcome, useAnnotationViewerAppryze } from './hook/use-annotation-viewer-appryze';
import { useStateViewerAppryze } from './hook/use-state-viewer-appryze';

type BufferAnnotationItemType = {
    // Resolves with whether the write landed. `void` is tolerated for entries
    // queued by callers that predate the outcome, and read as success.
    fn: () => Promise<CommitOutcome | void>;
    action: 'add' | 'modify' | 'delete';
};

// Suppress known noisy console warnings/errors from Chrome + WebViewer
if (typeof window !== 'undefined') {
    const win = window as any;
    if (!win.__SMX_CONSOLE_FILTER__) {
        win.__SMX_CONSOLE_FILTER__ = true;
        const originalWarn = console.warn;
        const originalError = console.error;

        const normalizeConsoleArgs = (args: any[]) => {
            const message = args.map((arg) => (typeof arg === 'string' ? arg : String(arg))).join(' ');
            const stack =
                args.find((arg) => arg && typeof arg === 'object' && typeof arg.stack === 'string')?.stack || '';
            return { message, stack };
        };

        const shouldSuppressByMessage = (message: string, stack = '', filename = '') => {
            if (!message && !stack && !filename) return false;
            return (
                message.includes('exposedSelectors') ||
                message.includes('Cannot read properties of undefined') ||
                message.includes('webviewer-core') ||
                stack.includes('webviewer-core') ||
                stack.includes('exposedSelectors') ||
                filename.includes('webviewer-core') ||
                filename.includes('exposedSelectors')
            );
        };

        const shouldSuppressConsole = (args: any[]) => {
            const { message, stack } = normalizeConsoleArgs(args);
            if (!message && !stack) return false;
            return (
                message.includes('message handler took') ||
                message.includes('document.write') ||
                (message.includes('net::ERR_METHOD_NOT_SUPPORTED') &&
                    message.includes('blob:') &&
                    message.includes('HEAD')) ||
                shouldSuppressByMessage(message, stack)
            );
        };

        console.warn = (...args: any[]) => {
            if (shouldSuppressConsole(args)) return;
            originalWarn.apply(console, args);
        };

        console.error = (...args: any[]) => {
            if (shouldSuppressConsole(args)) return;
            originalError.apply(console, args);
        };

        window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
            const message = event.reason?.message || event.reason?.toString() || '';
            const stack = event.reason?.stack || '';
            if (
                message.includes('Cannot read properties of undefined') ||
                message.includes('forEach') ||
                stack.includes('webviewer-core') ||
                stack.includes('exposedSelectors')
            ) {
                event.preventDefault();
                event.stopPropagation();
            }
        });

        window.addEventListener('error', (event: ErrorEvent) => {
            const message = event.message || '';
            const filename = event.filename || '';
            if (
                message.includes('Cannot read properties of undefined') ||
                filename.includes('webviewer-core') ||
                filename.includes('exposedSelectors')
            ) {
                event.preventDefault();
                event.stopPropagation();
            }
        });
    }
}

export type ViewerAppryzeContextType = 'drawing' | 'document' | 'attachmentScheduleItem' | 'attachmentTask';

export type ViewerAppryzeProps = {
    file: string | File | null;
    fileName?: string;
    height?: string;
    toolBar?: (instance: WebViewerInstance) => void;
    sheetData?: DrawingSheetOrDocumentIdDataType;
    displaySpinner?: boolean;
    context: ViewerAppryzeContextType;
    // Override the context-driven edit-tier permission set. When set, ViewerAppryze
    // uses these permissions for the view-only/edit decision (Apryse read-only mode,
    // toolbar selection, annotation save gating) instead of the context default.
    // Useful for consumers whose permission model differs from the library defaults.
    savePermissions?: PermissionCombinationType[];
    allowedAnnotation?: boolean;
    bufferAnnotation?: boolean;
    // `succeeded` is false when any buffered write failed, errored or timed out.
    // Consumers that cut a new document version on save must check it: doing so
    // after a failed write flattens the state the server still holds, not the
    // user's edits, and reports success.
    onSave?: (succeeded: boolean) => void;
    onUpdateAnnotations?: (flag: boolean) => void;
    triggerSave?: boolean;
    onInstanceReady?: (instance: WebViewerInstance | null) => void;
    onDocumentLoaded?: () => void;
};

export const getPermissionsList = (context: ViewerAppryzeContextType): PermissionCombinationType[] => {
    switch (context) {
        case 'drawing':
            return ['drawing.manage'];
        case 'document':
            return ['files.manage'];
        case 'attachmentScheduleItem':
            return ['work-item.manage'];
        case 'attachmentTask':
            return ['schedule-task.manage'];
        default:
            return [];
    }
};

// Permissions that count as "can save / edit". A user without any of these on the
// active context gets the library-enforced view-only treatment (read-only Apryse,
// minimal toolbar, no annotation save). Drawings consumers gate on drawing.*,
// documents consumers gate on files.* (the canonical group for document files).
// Consumers with custom permission models can override this entirely via the
// `savePermissions` prop on ViewerAppryze.
export const getSavingPermissionsList = (context: ViewerAppryzeContextType): PermissionCombinationType[] => {
    switch (context) {
        case 'drawing':
            return ['drawing.edit', 'drawing.manage'];
        case 'document':
            return ['files.edit', 'files.manage', 'files.project.edit', 'files.project.manage'];
        case 'attachmentScheduleItem':
            return ['work-item.edit', 'work-item.manage'];
        case 'attachmentTask':
            return ['schedule-task.edit', 'schedule-task.manage'];
        default:
            return [];
    }
};

const TYPE_IMAGE = 'image';

const TYPE_NONE = 'none';

export type ViewerAppryzeHandle = { save: () => void };

export const ViewerAppryze: React.FC<ViewerAppryzeProps> = ({
    file,
    fileName,
    toolBar,
    sheetData,
    height = '186px',
    context,
    savePermissions,
    allowedAnnotation = true,
    bufferAnnotation = false,
    displaySpinner = false,
    onSave = () => {},
    onUpdateAnnotations = () => {},
    triggerSave = false,
    onInstanceReady,
    onDocumentLoaded,
}) => {
    const webViewerRef = useRef<HTMLDivElement>(null);
    const isViewerInitialized = useRef<boolean>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [webViewer, setWebViewer] = useState<WebViewerInstance | null>(null);
    const intervalValueRef = useRef<number | NodeJS.Timeout>(0);
    const bufferAnnotationListRef = useRef<{ [key: string]: BufferAnnotationItemType[] }>({});
    const blobUrlCacheRef = useRef<Map<string, Blob>>(new Map());
    const autoSwitchRestoreRef = useRef<boolean | null>(null);
    // Refs so the image-download handler installed once in the WebViewer-init effect
    // reads the latest props instead of capturing them from first render.
    const fileRef = useRef(file);
    const fileNameRef = useRef(fileName);
    useEffect(() => {
        fileRef.current = file;
        fileNameRef.current = fileName;
    }, [file, fileName]);

    const { displayName } = useContext(SmxPermissionContext);

    const { setupStateListeners } = useStateViewerAppryze(webViewer);

    const fullDomain = window.location.origin;

    const permission = getPermissionsList(context);

    const accessManager = useAccess(permission);

    // View-only users (no manage/edit on this resource) get an Apryse-enforced read-only
    // viewer: no annotation interaction, but Download stays. Consumers can override the
    // permission set via the `savePermissions` prop; otherwise it falls back to the
    // context-driven default in getSavingPermissionsList.
    const accessSaving = useAccess(savePermissions ?? getSavingPermissionsList(context));

    const { setupAnnotationInterception, setCurrentDrawingSheetOrDocumentId } = useAnnotationViewerAppryze(
        webViewer,
        accessSaving,
        sheetData,
        bufferAnnotation,
        bufferAnnotationListRef,
        onUpdateAnnotations,
    );

    useEffect(() => {
        let instance: WebViewerInstance | null = null;
        let cleanupStateListeners: (() => void) | null = null;
        let cleanupAnnotationInterception: (() => void) | null = null;
        let onAnnotationSelectedHandler: ((annotations: Core.Annotations.Annotation[]) => void) | null = null;
        let saveModalObserver: MutationObserver | null = null;
        let cleanupDocumentLoaded: (() => void) | null = null;

        const initWebViewer = async () => {
            if (!webViewerRef.current) {
                return;
            }

            // setLoading(true);
            try {
                instance = await WebViewer.WebComponent(
                    {
                        licenseKey: API.APRYSE_LICENSE,
                        path: APRYZE_STATIC_URL,

                        isAdminUser: accessManager,
                        annotationUser: displayName,
                        enableAnnotations: true,
                        // fullAPI: true,
                        enableMeasurement: true,
                        isReadOnly: !accessSaving,
                        css: accessManager ? `${fullDomain}/style-manager.css` : `${fullDomain}/style.css`,
                        //
                        // enableRedaction: true,
                        // enableOptimizedWorkers: true,
                    },
                    webViewerRef.current,
                );

                instance.Core.annotationManager.enableRedaction();

                instance.UI.enableFeatures([instance.UI.Feature.Download]);
                instance.UI.enableElements(['downloadButton', 'downloadPresetButton']);

                instance.UI.disableElements([UI_ELEMENTS.PAGE_NAV_FLOATING_HEADER]);

                const { documentViewer } = instance.Core;
                const onDocumentLoadedHandler = () => {
                    const document = documentViewer.getDocument();
                    if (document && document.getType() === TYPE_IMAGE && instance) {
                        // Images are not meaningfully annotatable in our workflow — force view-only
                        // regardless of the user's permission tier. This overrides anything the
                        // consumer's toolBar set up earlier.
                        getViewOnlyToolBar(instance);
                        instance.UI.disableElements([UI_ELEMENTS.TOOLS_HEADER]);
                        try {
                            instance.Core.annotationManager.enableReadOnlyMode();
                        } catch {
                            // older Apryse builds without enableReadOnlyMode — ignore
                        }

                        const downloadOriginalImage = () => {
                            const currentFile = fileRef.current;
                            if (!currentFile) return;
                            const inferredName =
                                (currentFile instanceof File ? currentFile.name : undefined) ||
                                fileNameRef.current ||
                                'image';
                            const anchor = window.document.createElement('a');
                            let revokeUrl: string | null = null;
                            if (typeof currentFile === 'string') {
                                anchor.href = currentFile;
                            } else if (currentFile instanceof Blob) {
                                revokeUrl = URL.createObjectURL(currentFile);
                                anchor.href = revokeUrl;
                            } else {
                                return;
                            }
                            anchor.download = inferredName;
                            anchor.style.display = 'none';
                            window.document.body.appendChild(anchor);
                            anchor.click();
                            window.document.body.removeChild(anchor);
                            if (revokeUrl) {
                                setTimeout(() => URL.revokeObjectURL(revokeUrl!), 0);
                            }
                        };

                        // MainMenu (the class behind 'MainMenuFlyout') keeps its built-in items
                        // — Download included — outside the public `items` array. The only way to
                        // suppress those defaults reliably is disableFeatures, which also takes the
                        // keyboard shortcut down with it.
                        instance.UI.disableFeatures([instance.UI.Feature.Download]);
                        instance.UI.disableElements(['downloadButton', 'downloadPresetButton']);

                        try {
                            const flyout = instance.UI.Flyouts?.getFlyout?.('MainMenuFlyout');
                            if (flyout) {
                                const customItem = {
                                    dataElement: 'smxImageDownload',
                                    label: 'action.download',
                                    icon: 'icon-download',
                                    onClick: downloadOriginalImage,
                                };
                                // Drop any previous instance of our custom item, then append exactly
                                // one — idempotent if documentLoaded fires more than once.
                                const items = ((flyout.items as any[]) || []).filter(
                                    (it: any) => it?.dataElement !== 'smxImageDownload',
                                );
                                items.push(customItem);
                                flyout.setItems(items);
                            }
                        } catch {
                            // If the Flyouts API isn't available in this Apryse build, the
                            // disableFeatures call above still ensures no stale Download remains.
                        }
                    }
                    if (context === 'drawing') {
                        try {
                            const panTool = documentViewer.getTool('Pan');
                            if (panTool) {
                                documentViewer.setToolMode(panTool);
                            }
                        } catch {
                            // ignore tool mode errors
                        }
                    }
                    onDocumentLoaded?.();
                };
                documentViewer.addEventListener('documentLoaded', onDocumentLoadedHandler);
                cleanupDocumentLoaded = () => {
                    documentViewer.removeEventListener('documentLoaded', onDocumentLoadedHandler);
                };
                instance.UI.disableElements([UI_ELEMENTS.NOTES_PANEL]);
                instance.UI.disableElements([UI_ELEMENTS.NOTES_PANEL_HEADER]);

                const setupHideIncludeComments = () => {
                    const host = webViewerRef.current?.querySelector('apryse-webviewer');
                    const root = host?.shadowRoot as ShadowRoot | null;
                    if (!root) return;

                    const removeOption = () => {
                        const inputs = root.querySelectorAll('input[name="include-comment-option"]');
                        inputs.forEach((input) => {
                            if (!(input instanceof HTMLInputElement)) return;
                            const choice = input.closest('.ui__choice');
                            if (choice instanceof HTMLElement) {
                                choice.style.display = TYPE_NONE;
                            } else {
                                input.style.display = TYPE_NONE;
                                const label = input.nextElementSibling;
                                if (label instanceof HTMLLabelElement) {
                                    label.style.display = TYPE_NONE;
                                }
                            }
                        });
                    };

                    removeOption();
                    saveModalObserver = new MutationObserver(() => removeOption());
                    saveModalObserver.observe(root, { childList: true, subtree: true });
                };
                setupHideIncludeComments();

                const annotManager = instance.Core.annotationManager;
                const ui = instance.UI;

                onAnnotationSelectedHandler = (annotations: Core.Annotations.Annotation[]) => {
                    const Sticky = instance?.Core?.Annotations?.StickyAnnotation as any;
                    const isNoteSelected =
                        Array.isArray(annotations) && annotations.some((a) => Sticky && a instanceof Sticky);

                    if (isNoteSelected) {
                        ui.enableElements([UI_ELEMENTS.NOTES_PANEL, UI_ELEMENTS.NOTES_PANEL_HEADER]);
                    } else {
                        ui.disableElements([UI_ELEMENTS.NOTES_PANEL, UI_ELEMENTS.NOTES_PANEL_HEADER]);
                    }
                };
                annotManager.addEventListener('annotationSelected', onAnnotationSelectedHandler);

                cleanupStateListeners = setupStateListeners(instance);
                cleanupAnnotationInterception = allowedAnnotation
                    ? setupAnnotationInterception(instance) || null
                    : null;

                setWebViewer(instance);
            } catch (error) {
                console.error('Error initializing WebViewer:', error);
            } finally {
                setTimeout(() => {
                    // setLoading(false);
                }, 500);
            }
        };

        if (!isViewerInitialized.current) {
            isViewerInitialized.current = true;
            initWebViewer();
        }

        return () => {
            cleanupStateListeners?.();
            cleanupAnnotationInterception?.();
            saveModalObserver?.disconnect();
            cleanupDocumentLoaded?.();

            if (instance) {
                const annotManager = instance.Core?.annotationManager;
                if (onAnnotationSelectedHandler) {
                    annotManager?.removeEventListener?.('annotationSelected', onAnnotationSelectedHandler);
                }
                instance.UI.closeDocument();
                setWebViewer(null);
                isViewerInitialized.current = false;
            }
        };
    }, []);

    useEffect(() => {
        if (onInstanceReady) {
            onInstanceReady(webViewer ?? null);
        }
        return () => {
            if (onInstanceReady) {
                onInstanceReady(null);
            }
        };
    }, [webViewer, onInstanceReady]);

    useEffect(() => {
        if (!webViewer || context !== 'drawing') return;

        const tool = webViewer.Core?.Tools?.Tool;
        if (!tool) return;

        if (autoSwitchRestoreRef.current === null) {
            autoSwitchRestoreRef.current = tool.ENABLE_AUTO_SWITCH;
        }

        tool.disableAutoSwitch();

        return () => {
            if (autoSwitchRestoreRef.current === null) return;
            if (autoSwitchRestoreRef.current) {
                tool.enableAutoSwitch();
            } else {
                tool.disableAutoSwitch();
            }
        };
    }, [webViewer, context]);

    useEffect(() => {
        if (!webViewer) return;

        if (!accessSaving) {
            // View-only → library-controlled minimal toolbar: hamburger (Download), zoom, View ribbon.
            // Consumer's toolBar is intentionally NOT called: it would add annotation/edit tools.
            getViewOnlyToolBar(webViewer);
            webViewer.UI.disableElements([UI_ELEMENTS.TOOLS_HEADER]);
        } else if (toolBar) {
            // Editor/manager + consumer-provided toolbar → full consumer chrome.
            toolBar(webViewer);
        } else {
            // Editor/manager but consumer passed no toolBar → keep the existing minimal mode.
            webViewer.UI.disableElements([UI_ELEMENTS.DEFAULT_TOP_HEADER]);
            webViewer.UI.disableElements([UI_ELEMENTS.TOOLS_HEADER]);
        }

        webViewer.UI.disableElements([UI_ELEMENTS.PAGE_NAV_FLOATING_HEADER]);
    }, [webViewer, toolBar, accessSaving]);

    useEffect(() => {
        if (!webViewer || !file) return;

        const resolveDocumentInput = async (): Promise<string | File | Blob> => {
            if (typeof file === 'string' && file.startsWith('blob:')) {
                const cached = blobUrlCacheRef.current.get(file);
                if (cached) return cached;

                try {
                    const response = await fetch(file);
                    const blob = await response.blob();
                    blobUrlCacheRef.current.set(file, blob);
                    return blob;
                } catch {
                    return file;
                }
            }

            return file;
        };

        const loadDocument = async () => {
            setLoading(true);
            try {
                const documentInput = await resolveDocumentInput();
                webViewer.UI.loadDocument(documentInput, {
                    filename: (file as File)?.name || fileName,
                });

                intervalValueRef.current = setInterval(() => {
                    if (webViewer.Core.documentViewer.getDocument()) {
                        // Loading existing markup and form values is a read, so it is
                        // not gated on allowedAnnotation: a view-only user still has to
                        // see them. Only the write path (setupAnnotationInterception) is.
                        setCurrentDrawingSheetOrDocumentId(sheetData?.sheetId || sheetData?.documentId);

                        clearInterval(intervalValueRef.current);
                        setLoading(false);
                    }
                }, 200);
            } catch (error) {
                console.error('Error loading document:', error);
            }
        };

        void loadDocument();
    }, [webViewer, file]);

    const handleSave = async () => {
        // Every buffered write has to land before a consumer may cut a new
        // flattened version; one failure makes the whole flush unsafe to build on.
        let allSucceeded = true;

        for (const key of Object.keys(bufferAnnotationListRef.current)) {
            let itemBuffer: BufferAnnotationItemType[] = bufferAnnotationListRef.current[key];
            if (bufferAnnotationListRef.current[key].length > 1) {
                const firstItem = bufferAnnotationListRef.current[key]?.[0];
                const lastItem = (bufferAnnotationListRef.current[key]?.[
                    bufferAnnotationListRef.current[key].length - 1
                ] || {}) as BufferAnnotationItemType;
                // Cases we need to skip
                if (!firstItem || (firstItem.action === 'add' && lastItem.action === 'delete')) {
                    continue;
                }

                // Modifications reduced to the last one
                if (firstItem.action === 'add' && lastItem.action === 'modify') {
                    itemBuffer = [firstItem, lastItem];
                } else if (lastItem.action === 'modify') {
                    itemBuffer = [lastItem];
                }
                // If delete, then only delete
                if (lastItem.action === 'delete') {
                    itemBuffer = [lastItem];
                }
            }
            for (const item of itemBuffer) {
                try {
                    const outcome = await item.fn();
                    if (outcome && outcome.ok === false) {
                        allSucceeded = false;
                        console.error('Annotation write did not land:', outcome.reason);
                    }
                    if (item.action === 'add') await new Promise((resolve) => setTimeout(resolve, 1000));
                } catch (error) {
                    allSucceeded = false;
                    console.error('Error saving annotation:', error);
                }
            }
        }
        onSave(allSucceeded);
    };

    useEffect(() => {
        if (triggerSave === true) handleSave();
    }, [triggerSave]);

    return (
        <>
            <div
                ref={webViewerRef}
                style={{
                    width: '100%',
                    height,
                }}
            />
            {displaySpinner && <Spin spinning={loading} fullscreen={true}></Spin>}
        </>
    );
};
